import React from 'react'
import { useTheme } from '../hooks/useTheme'
import { themeLabels, themeDescriptions, getTheme } from '../config/themes'

interface ThemeSelectorProps {
  className?: string
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ className = '' }) => {
  const { currentTheme, setTheme, allThemes, colors } = useTheme()

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Tema de la aplicación</h3>
        <p className="text-xs text-gray-500">Elige la paleta de colores que más te guste</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {allThemes.map((theme) => {
          const isActive = currentTheme === theme
          const themeColors = getTheme(theme)
          
          return (
            <button
              key={theme}
              onClick={() => setTheme(theme)}
              className={`
                relative overflow-hidden rounded-xl p-4 text-left transition-all duration-300
                ${isActive
                  ? 'ring-2 ring-offset-2 shadow-lg scale-[1.02]'
                  : 'hover:shadow-md hover:scale-[1.01] border-2 border-gray-200'
                }
              `}
              style={{
                backgroundColor: isActive ? themeColors.background : '#f9fafb',
                ringColor: isActive ? themeColors.primary : undefined,
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <h4 
                  className="text-sm font-bold"
                  style={{ color: themeColors.text }}
                >
                  {themeLabels[theme]}
                </h4>
                {isActive && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{
                    backgroundColor: themeColors.primary,
                    color: 'white'
                  }}>
                    Activo
                  </span>
                )}
              </div>

              {/* Descripción */}
              <p 
                className="text-xs mb-3"
                style={{ color: themeColors.textSecondary }}
              >
                {themeDescriptions[theme]}
              </p>

              {/* Preview de colores */}
              <div className="flex gap-1.5">
                {[
                  { color: themeColors.primary, label: 'P' },
                  { color: themeColors.secondary, label: 'S' },
                  { color: themeColors.accent, label: 'A' },
                  { color: themeColors.success, label: 'ok' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="w-8 h-8 rounded-md border border-gray-300 flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
                    style={{ backgroundColor: item.color }}
                    title={item.label}
                  />
                ))}
              </div>
            </button>
          )
        })}
      </div>

      {/* Preview detallado del tema activo */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-xs font-semibold text-gray-700 mb-3">Vista previa del tema actual:</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: 'Primary', color: colors.primary },
            { label: 'Secondary', color: colors.secondary },
            { label: 'Accent', color: colors.accent },
            { label: 'Success', color: colors.success },
            { label: 'Warning', color: colors.warning },
            { label: 'Error', color: colors.error },
            { label: 'Text', color: colors.text },
            { label: 'Border', color: colors.border },
          ].map((item) => (
            <div key={item.label} className="flex flex-col gap-1">
              <div
                className="w-full h-10 rounded-lg border border-gray-300 shadow-sm"
                style={{ backgroundColor: item.color }}
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-medium text-gray-600">{item.label}</p>
                <p className="text-[9px] font-mono text-gray-400">{item.color}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Nota sobre el tema Signature */}
      {currentTheme === 'tema5' && (
        <div className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
          <p className="text-xs text-blue-900">
            <strong>⭐ Tema Signature:</strong> Paleta profesional recomendada, 
            equilibrada entre azules naturales del lago y verdes de la naturaleza.
          </p>
        </div>
      )}
    </div>
  )
}

export default ThemeSelector
